import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

interface EnhancedBackButtonProps {
  className?: string;
  variant?: 'icon' | 'text' | 'full';
  size?: 'sm' | 'default' | 'lg';
  showDepth?: boolean;
  fallbackPath?: string;
  onBack?: () => void;
}

const EnhancedBackButton: React.FC<EnhancedBackButtonProps> = ({
  className,
  variant = 'icon',
  size = 'default',
  showDepth = false,
  fallbackPath,
  onBack
}) => {
  const { goBack, getBackPath, getBackTitle, navigationState } = useNavigation();
  const navigate = useNavigate();

  const getSmartFallbackContext = () => {
    // Smart fallback detection based on URL patterns
    const currentPath = window.location.pathname;
    const referrer = document.referrer;
    
    if (currentPath.includes('/profile/')) {
      // Check session storage for context
      const lastGroupName = sessionStorage.getItem('lastGroupName');
      const lastMatchesPage = sessionStorage.getItem('lastMatchesPage');
      
      if (lastGroupName) {
        return { title: lastGroupName, path: `/group/${sessionStorage.getItem('lastGroupId')}` };
      }
      
      if (lastMatchesPage) {
        return { title: 'Matches', path: '/matches' };
      }
      
      // Fallback to referrer analysis
      if (referrer) {
        const referrerUrl = new URL(referrer);
        if (referrerUrl.pathname.includes('/group/')) {
          return { title: 'Group', path: referrerUrl.pathname };
        }
        if (referrerUrl.pathname === '/matches') {
          return { title: 'Matches', path: '/matches' };
        }
      }
    }
    return null;
  };

  const handleBack = () => {
    // Clear navigation context when going back
    sessionStorage.removeItem('lastGroupName');
    sessionStorage.removeItem('lastGroupId');
    sessionStorage.removeItem('lastMatchesPage');
    
    if (onBack) {
      onBack();
      return;
    }

    if (navigationState.canGoBack || getBackPath()) {
      goBack();
    } else if (fallbackPath) {
      navigate(fallbackPath);
    } else {
      // Smart fallback navigation
      const fallbackContext = getSmartFallbackContext();
      if (fallbackContext) {
        navigate(fallbackContext.path);
      } else {
        window.history.back();
      }
    }
  };

  const backTitle = getBackTitle();
  const smartFallback = getSmartFallbackContext();
  const canGoBack = navigationState.canGoBack || !!getBackPath();
  
  // Always show back button with appropriate context
  const showBackButton = canGoBack || fallbackPath || smartFallback || window.history.length > 1;

  const getButtonContent = () => {
    switch (variant) {
      case 'icon':
        return <ArrowLeft className="h-4 w-4" />;
      case 'text':
        return (
          <div className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4" />
            <span>Back</span>
          </div>
        );
      case 'full':
        return (
          <div className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4" />
            <span>
              {backTitle ? `Back to ${backTitle}` : 
               smartFallback ? `Back to ${smartFallback.title}` : 
               'Back'}
              {showDepth && navigationState.depth > 1 && (
                <span className="text-xs opacity-70 ml-1">
                  ({navigationState.depth - 1})
                </span>
              )}
            </span>
          </div>
        );
      default:
        return <ArrowLeft className="h-4 w-4" />;
    }
  };

  if (!showBackButton) {
    return null;
  }

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={handleBack}
      className={cn(
        "transition-all duration-200 hover:bg-muted/50",
        variant === 'icon' && "p-2",
        className
      )}
      aria-label={
        backTitle ? `Back to ${backTitle}` : 
        smartFallback ? `Back to ${smartFallback.title}` : 
        'Go back'
      }
    >
      {getButtonContent()}
    </Button>
  );
};

export default EnhancedBackButton;